import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Drawer } from "vaul";

import {
  isBottomCtaSourceOccupied,
  subscribeBottomCtaOccupied,
} from "@/components/care/transparentCare/bottomCtaOccupancy";
import ChatConsent from "@/components/chat/ChatConsent";
import ChatDisclaimer from "@/components/chat/ChatDisclaimer";
import ChatHeader from "@/components/chat/ChatHeader";
import ChatHistory from "@/components/chat/ChatHistory";
import ChatInput from "@/components/chat/ChatInput";
import ChatMessage from "@/components/chat/ChatMessage";
import SparkleIcon from "@/components/chat/SparkleIcon";
import { AuthContext } from "@/contexts/authContext";
import { useChat } from "@/contexts/chatContext";
import { useSavedCardRefresh } from "@/hooks/useSavedCardRefresh";
import {
  getSessionHistory,
  streamAIMessage,
  type ContentBlock,
  type HistoryMessage,
  type ResponseMetadata,
  type SourceItem,
} from "@/services/aiService";
import {
  DESTINATION_FULLSTORY,
  DESTINATION_POSTHOG,
  trackCustomEvent,
} from "@/utils/analytics/tracking";
import { cn } from "@/utils/cn";

function formatTimestamp(): string {
  return new Date().toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

const LOADING_MESSAGES = [
  "Gathering details...",
  "Reviewing your results...",
  "Looking at the latest research...",
];

function LoadingIndicator() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-[var(--chat-space-03)]">
      <SparkleIcon className="w-[17px] h-[20px] animate-pulse shrink-0" />
      <span className="text-[14px] font-normal text-[var(--chat-grey-500)] leading-[1.5]">
        {LOADING_MESSAGES[index]}
      </span>
    </div>
  );
}

export default function ChatSidebar() {
  const chat = useChat();
  const authContext = useContext(AuthContext);
  // The mounted cart panel's own report, not showCart: showCart can be true on a page
  // with no panel (no header), where hiding the chat would leave no way back to it.
  const cartPanelOpen = useSyncExternalStore(subscribeBottomCtaOccupied, () =>
    isBottomCtaSourceOccupied("cart-panel")
  );
  const user = authContext.currentUser;
  const refreshedCards = useSavedCardRefresh(
    // A cut turn's recovered card too, under its own key so its refresh never resets live taps.
    chat?.viewingHistorySessionId ??
      (chat?.sessionId ? `${chat.sessionId}:recovered` : null),
    chat?.messages ?? [],
    !!chat?.isOpen && !cartPanelOpen
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track whether user has scrolled away from bottom during streaming
  const userScrolledRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const prevMessageCountRef = useRef(0);
  const didInitialScrollRef = useRef(false);

  // Mobile bottom sheet snap points:
  //   "140px" — minimized peek (header bar + drag-handle area + safe-area
  //             inset on devices with a home indicator). Always taller
  //             than 100px so there's a visible target to grab when
  //             dragging back up.
  //   0.5     — half-height.
  //   1       — fullscreen. Default initial state on first open.
  // The drawer is non-dismissible — drag-down can't go below the peek,
  // and only the close button in ChatHeader actually closes the drawer.
  const SNAP_POINTS = ["140px", 0.5, 1] as const;
  const INITIAL_SNAP = SNAP_POINTS[2];
  const [snapPoint, setSnapPoint] = useState<number | string | null>(
    INITIAL_SNAP
  );

  // Reset to the initial (full) snap whenever the drawer opens, so
  // reopening always lands in a predictable state regardless of where
  // the user dragged it last time.
  useEffect(() => {
    if (chat?.isOpen) setSnapPoint(INITIAL_SNAP);
  }, [chat?.isOpen, INITIAL_SNAP]);

  // Keyboard sizing is handled by the `interactive-widget=resizes-content`
  // viewport meta in public/index.html plus vaul's snap layout, so we
  // don't intervene programmatically on input focus.

  // Mobile history panel visibility (separate from the snap state above).
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);

  // Match the Tailwind sm: breakpoint so we know when to render the
  // mobile bottom-sheet (vaul) vs. the desktop right-sidebar.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 639px)");
    setIsMobile(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const isStaff =
    process.env.NODE_ENV === "development" ||
    (user?.identity?.isStaff ?? false);

  // Scroll behavior:
  // - Initial mount with existing messages (reopen): land at the bottom so
  //   the user picks up where the last assistant reply ended.
  // - Batch load (e.g., loadSession populating history): land at the top of the
  //   conversation so the user reads from the start, not the middle.
  // - Single message append (user send / AI response): scroll the new last
  //   message to the top of the viewport.
  useEffect(() => {
    const count = chat?.messages?.length ?? 0;
    const prev = prevMessageCountRef.current;
    const delta = count - prev;

    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      prevMessageCountRef.current = count;
      if (count > 0) {
        // Drawer-open animation + late layout (markdown, source cards) can
        // leave scrollHeight stale right after mount, so jump to the end
        // immediately and re-anchor after the drawer settles.
        const jumpToEnd = () => {
          const end = messagesEndRef.current;
          const container = scrollContainerRef.current;
          if (end) {
            end.scrollIntoView({ block: "end" });
          } else if (container) {
            container.scrollTop = container.scrollHeight;
          }
        };
        requestAnimationFrame(jumpToEnd);
        setTimeout(jumpToEnd, 350);
      }
      return;
    }

    if (delta > 1) {
      // Bulk load (e.g., loadSession) — show the start of the conversation.
      requestAnimationFrame(() => {
        const container = scrollContainerRef.current;
        if (container) container.scrollTop = 0;
      });
    } else if (delta === 1) {
      const lastMsg = chat?.messages?.[count - 1];
      const isAssistant = lastMsg?.role !== "user";
      // For assistant messages, respect a user who has scrolled away mid-stream.
      if (isAssistant && userScrolledRef.current) {
        prevMessageCountRef.current = count;
        return;
      }
      requestAnimationFrame(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const messageEls = container.querySelectorAll("[data-message-index]");
        const lastEl = messageEls[messageEls.length - 1];
        if (lastEl) {
          lastEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }

    prevMessageCountRef.current = count;
  }, [chat?.messages?.length]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleSend = useCallback(
    async (content: string) => {
      if (!chat || chat.isLoading || !chat.sessionId) return;
      const sid = chat.sessionId;
      chat.setIsLoading(true);
      chat.setStreamingContent("");

      const timestamp = formatTimestamp();

      chat.setMessages((prev) => [
        ...prev,
        { role: "user", content, timestamp },
      ]);

      trackCustomEvent(
        "ai_chat_message_sent",
        { message_length: content.length },
        [DESTINATION_FULLSTORY, DESTINATION_POSTHOG]
      );

      try {
        let fullContent = "";
        let metadata: ResponseMetadata | null = null;

        const blocks: ContentBlock[] = [];
        let currentText = "";

        abortControllerRef.current = new AbortController();
        for await (const event of streamAIMessage(
          sid,
          content,
          abortControllerRef.current.signal,
          chat.viewingContext
        )) {
          if (event.type === "guardrail_blocked") {
            // Replace any streamed content with a safe message
            fullContent =
              event.replacement ||
              "I'm not able to answer that question. Please try rephrasing, or reach out to our coaching team for personalized guidance.";
            currentText = "";
            blocks.length = 0;
            chat.setStreamingContent("");
          } else if (event.type === "chunk") {
            fullContent += event.data;
            currentText += event.data;
            chat.setStreamingContent(fullContent);
          } else if (event.type === "block") {
            if (currentText) {
              blocks.push({ type: "text", content: currentText });
              currentText = "";
            }
            blocks.push(event.data);
          } else if (event.type === "metadata") {
            metadata = event.data;
          }
        }

        if (currentText) {
          blocks.push({ type: "text", content: currentText });
        }

        chat.setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: fullContent,
            blocks: blocks.length > 0 ? blocks : undefined,
            metadata,
          },
        ]);
        chat.setStreamingContent("");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        chat.setStreamingContent("");
        const cutNotice = {
          role: "assistant" as const,
          content: "Sorry, something went wrong. Please try again.",
        };
        chat.setMessages((prev) => [...prev, cutNotice]);
        // evvy-ai can still save a cut turn with its card counted as shown, so show what it saved.
        getSessionHistory(sid)
          .then((history) => {
            const [asked, answer] = history.slice(-2);
            if (
              asked?.role !== "user" ||
              asked.content !== content ||
              answer?.role !== "assistant"
            )
              return;
            // A retried question's unsaved cut still ends history with the earlier answer.
            const isCutQuestion = (m: HistoryMessage) =>
              m.role === "user" && m.content === content;
            // A send or a view switch since the cut moves the notice off the end; leave those alone.
            chat.setMessages((prev) =>
              prev[prev.length - 1] === cutNotice &&
              history.filter(isCutQuestion).length >=
                prev.filter(isCutQuestion).length
                ? [...prev.slice(0, -1), answer, cutNotice]
                : prev
            );
          })
          .catch(() => {});
      } finally {
        abortControllerRef.current = null;
        chat.setIsLoading(false);
      }
    },
    [chat]
  );

  // Sends a chip's question once the session is live. The ref stops a
  // re-render from double-sending before the context state clears.
  const autoSentRef = useRef(false);
  useEffect(() => {
    const prompt = chat?.pendingPrompt;
    if (!prompt) {
      autoSentRef.current = false;
      return;
    }
    if (
      autoSentRef.current ||
      !chat.sessionId ||
      chat.isCreatingSession ||
      chat.isLoading ||
      chat.isLoadingHistory ||
      chat.needsConsent ||
      chat.viewingHistorySessionId
    ) {
      return;
    }
    autoSentRef.current = true;
    chat.clearPendingPrompt();
    handleSend(prompt);
  }, [chat, handleSend]);

  const handleExpandSources = useCallback(
    (sources: SourceItem[]) => {
      if (!chat) return;
      // Prevent duplicate expansion
      chat.setMessages((prev) => {
        if (prev[prev.length - 1]?.isSourceExpansion) return prev;
        return [
          ...prev,
          {
            role: "assistant" as const,
            content: "",
            blocks: [
              {
                type: "text" as const,
                content:
                  "Here are the sources referenced in the last response:",
              },
              { type: "sources" as const, items: sources },
            ],
            isSourceExpansion: true,
          },
        ];
      });
    },
    [chat]
  );

  // The minicart takes the foreground; the conversation stays open in context and
  // returns when the cart closes. Returning null keeps this mounted, so a stream continues.
  if (!chat || !chat.isOpen || cartPanelOpen) return null;

  const body = (
    <>
      {/* Header */}
      <ChatHeader
        isExpanded={chat.isExpanded}
        onToggleExpand={chat.toggleExpanded}
        onClose={chat.close}
        mobileHistoryOpen={mobileHistoryOpen}
        onToggleMobileHistory={() => setMobileHistoryOpen((v) => !v)}
      />

      {/* Body: history sidebar (expanded only) + conversation */}
      <div className="flex flex-1 min-h-0">
        {/* Conversation history — expanded mode or mobile history panel */}
        {(chat.isExpanded || mobileHistoryOpen) && (
          <ChatHistory
            sessions={chat.sessions}
            activeSessionId={chat.sessionId}
            viewingHistorySessionId={chat.viewingHistorySessionId}
            onSelectSession={(id) => {
              chat.loadSession(id);
              setMobileHistoryOpen(false);
            }}
            onReturnToLive={() => {
              chat.returnToLive();
              setMobileHistoryOpen(false);
            }}
          />
        )}

        {/* Main conversation area — hidden on mobile when history is open */}
        <div
          className={cn(
            "flex flex-1 flex-col min-h-0",
            mobileHistoryOpen && "hidden sm:flex"
          )}
        >
          {/* Consent screen */}
          {chat.needsConsent ? (
            <div
              className={cn(
                "flex-1 overflow-y-auto",
                "flex flex-col items-start",
                chat.isExpanded
                  ? "px-[40px] pt-[var(--chat-space-06)] max-w-[640px] mx-auto w-full"
                  : "px-[16px] pt-[16px] sm:px-[var(--chat-space-06)] sm:pt-[var(--chat-space-06)] w-[403px] max-w-full mx-auto"
              )}
            >
              <ChatConsent
                onAccept={chat.handleAcceptConsent}
                onCancel={chat.close}
                isLoading={chat.isAcceptingConsent}
              />
            </div>
          ) : (
            <>
              {/* Messages */}
              <div
                ref={scrollContainerRef}
                onScroll={() => {
                  const el = scrollContainerRef.current;
                  if (!el) return;
                  const hasScrollableContent =
                    el.scrollHeight > el.clientHeight + 50;
                  // User is "at bottom" if within 100px of the end
                  const atBottom =
                    el.scrollHeight - el.scrollTop - el.clientHeight < 100;
                  userScrolledRef.current = !atBottom;
                  setShowScrollButton(hasScrollableContent && !atBottom);
                }}
                style={{
                  WebkitMaskImage:
                    "linear-gradient(to bottom, black 0%, black 85%, transparent 100%)",
                  maskImage:
                    "linear-gradient(to bottom, black 0%, black 85%, transparent 100%)",
                }}
                className={cn(
                  "flex-1 overflow-y-auto overscroll-contain touch-auto",
                  "flex flex-col gap-[var(--chat-space-05)] items-start",
                  "pb-[40px]",
                  chat.isExpanded
                    ? "px-[40px] pt-[var(--chat-space-06)] max-w-[640px] mx-auto w-full"
                    : "px-[16px] pt-[16px] sm:px-[var(--chat-space-06)] sm:pt-[var(--chat-space-06)] w-[403px] max-w-full mx-auto"
                )}
              >
                {chat.isCreatingSession && (
                  <div className="flex flex-col items-center gap-[var(--chat-space-04)] mt-12 w-full">
                    <SparkleIcon className="w-[24px] h-[28px] animate-pulse" />
                    <p className="text-[14px] text-[var(--chat-grey-500)] leading-[1.5] text-center">
                      Getting your results ready...
                    </p>
                  </div>
                )}

                {chat.sessionError && (
                  <div className="text-center text-[var(--chat-text-base)] mt-8 w-full">
                    <p className="text-red-600">
                      Couldn&apos;t start a chat session.
                    </p>
                    <button
                      onClick={() => {
                        // close() drops the pending question, so carry it over.
                        const retryPrompt = chat.pendingPrompt;
                        chat.close();
                        setTimeout(() => {
                          if (retryPrompt) chat.openWithPrompt(retryPrompt);
                          else chat.open();
                        }, 50);
                      }}
                      className="mt-2 text-[var(--chat-pine)] underline"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {!chat.isCreatingSession &&
                  !chat.sessionError &&
                  chat.messages.length === 0 &&
                  !chat.streamingContent && (
                    <div className="text-center text-[var(--chat-grey-500)] text-[var(--chat-text-base)] mt-8 w-full">
                      <p>
                        Hi! Ask me anything about your vaginal health results or
                        care plan.
                      </p>
                    </div>
                  )}

                {chat.messages.map((msg, i) => {
                  // Show timestamp before first message or when switching from assistant to user
                  const showTimestamp =
                    msg.timestamp &&
                    (i === 0 || chat.messages[i - 1]?.role === "assistant");

                  return (
                    <div
                      key={i}
                      data-message-index={i}
                      className="w-full flex flex-col gap-[var(--chat-space-04)]"
                    >
                      {showTimestamp && (
                        <p className="text-[var(--chat-text-sm)] font-semibold leading-[1.2] text-[var(--chat-grey-500)] text-center uppercase tracking-[0.65px] w-full">
                          {msg.timestamp}
                        </p>
                      )}
                      <ChatMessage
                        role={msg.role}
                        content={msg.content}
                        blocks={msg.blocks}
                        messageIndex={i}
                        sessionId={chat.sessionId || ""}
                        refreshedCards={refreshedCards}
                        refreshedSource={
                          chat.viewingHistorySessionId ? "history" : "live"
                        }
                        metadata={msg.metadata}
                        isStaff={isStaff}
                        onExpandSources={
                          msg.isSourceExpansion
                            ? undefined
                            : handleExpandSources
                        }
                      />
                    </div>
                  );
                })}

                {(chat.isLoading || chat.isLoadingHistory) &&
                  !chat.streamingContent && <LoadingIndicator />}

                {chat.streamingContent && (
                  <ChatMessage
                    role="assistant"
                    content={chat.streamingContent}
                    messageIndex={chat.messages.length}
                    sessionId={chat.sessionId || ""}
                    isStreaming
                  />
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Scroll to bottom button */}
              {showScrollButton && (
                <div className="flex justify-center py-1 shrink-0">
                  <button
                    onClick={() => {
                      messagesEndRef.current?.scrollIntoView({
                        behavior: "smooth",
                      });
                      setShowScrollButton(false);
                    }}
                    className="w-6 h-6 rounded-full bg-[var(--chat-black)] flex items-center justify-center hover:opacity-80 transition-opacity"
                    aria-label="Scroll to bottom"
                  >
                    <svg width="12" height="7" viewBox="0 0 12 7" fill="none">
                      <path
                        d="M1 1L6 6L11 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {/* Input + Disclaimer */}
              <div
                className={cn(
                  "w-full",
                  chat.isExpanded && "max-w-[640px] mx-auto"
                )}
              >
                {chat.viewingHistorySessionId ? (
                  <div className="px-[var(--chat-space-06)] py-[var(--chat-space-05)] text-center">
                    <p className="text-[14px] text-[var(--chat-grey-500)] mb-[var(--chat-space-03)]">
                      Viewing past conversation
                    </p>
                    <button
                      onClick={chat.returnToLive}
                      className="text-[14px] text-[var(--chat-pine)] font-medium hover:opacity-70 transition-opacity"
                    >
                      Return to current chat
                    </button>
                  </div>
                ) : (
                  <ChatInput
                    onSend={handleSend}
                    disabled={
                      chat.isLoading ||
                      chat.isCreatingSession ||
                      chat.sessionError
                    }
                    className={chat.isExpanded ? "px-[40px]" : undefined}
                  />
                )}

                {/* Disclaimer */}
                <div
                  className={cn(
                    "pb-[32px] sm:pb-[var(--chat-space-07)]",
                    chat.isExpanded
                      ? "px-[40px]"
                      : "px-[16px] sm:px-[var(--chat-space-06)]"
                  )}
                >
                  <ChatDisclaimer />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );

  // Mobile bottom sheet (vaul). Desktop sidebar and expanded full-screen
  // mode keep the previous fixed-positioned layout — vaul's modal-style
  // drawer is for mobile only, where its drag/snap UX is the win.
  const sharedShellClass = cn(
    "flex flex-col overscroll-none",
    "bg-[rgba(255,255,255,0.9)] backdrop-blur-[20px]",
    "border border-[var(--chat-grey-100)]",
    "shadow-[2px_0px_4px_0px_rgba(112,115,118,0.15),-2px_-2px_4px_0px_rgba(112,115,118,0.15)]"
  );

  if (isMobile && !chat.isExpanded) {
    return (
      <Drawer.Root
        open={chat.isOpen}
        onOpenChange={(o) => {
          // Drag-dismiss is gated by `dismissible={false}`. If vaul ever
          // fires onOpenChange(false) anyway (overlay click, escape key,
          // edge cases) snap to the peek instead of actually closing —
          // the chat must always be reachable on mobile. The explicit
          // close button calls chat.close() directly and bypasses this.
          if (!o) setSnapPoint(SNAP_POINTS[0]);
        }}
        snapPoints={[...SNAP_POINTS]}
        activeSnapPoint={snapPoint}
        setActiveSnapPoint={setSnapPoint}
        dismissible={false}
        repositionInputs={false}
      >
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-40 bg-black/40" />
          <Drawer.Content
            className={cn(
              sharedShellClass,
              "fixed inset-x-0 bottom-0 z-50 rounded-t-[20px] outline-none",
              // Cap the drawer height to the dynamic viewport minus the
              // top safe-area inset, so the header is never clipped by
              // the iOS notch / Dynamic Island on full-screen snaps.
              "max-h-[calc(100dvh-env(safe-area-inset-top))]",
              // Reserve space for the iOS home-indicator / Android nav bar
              // so the disclaimer at the bottom of the body isn't clipped.
              "pb-[env(safe-area-inset-bottom)]"
            )}
          >
            <div
              aria-hidden
              className="mx-auto my-[20px] h-[5px] w-16 rounded-full bg-[var(--chat-grey-100)]"
            />
            {body}
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  return (
    <div
      className={cn(
        sharedShellClass,
        "fixed z-50",
        chat.isExpanded
          ? "inset-0"
          : "sm:top-0 sm:right-0 sm:left-auto sm:bottom-auto sm:h-full sm:w-[var(--chat-sidebar-width)] sm:max-w-full sm:rounded-none"
      )}
    >
      {body}
    </div>
  );
}
